import convertTimeStringToSeconds from '@/lib/convert-time-string-to-seconds'
import { stripSubtitlePunctuation } from '@/lib/subtitles/stripSubtitlePunctuation'

interface Word {
  word: string
  startTime: string
  endTime: string
}

interface Segment {
  startTime: string
  endTime: string
  words: Word[]
  id?: number
  text?: string
}

interface Transcription {
  segments: Segment[]
}

interface Project {
  subtitle: {
    transcription: Transcription
  }
}

interface ReorganizeOptions {
  maxWordsPerLine: number
  maxSecondsPerLine: number
  maxCharactersPerLine: number
  cutPhrases: boolean
  removePunctuation: boolean
}

const WORD_END_CHARACTERS = new Set(['?', '.', '!'])

const reorganizeWords = (
  project: Project,
  options: ReorganizeOptions
): Segment[] => {
  const { maxWordsPerLine, maxSecondsPerLine, maxCharactersPerLine, cutPhrases, removePunctuation } = options
  const reorganizedSegments: Segment[] = []

  let currentSegment: Segment = {
    startTime: '',
    endTime: '',
    words: [],
  }

  const words = project.subtitle.transcription.segments.map((l) => l.words).flat()

  let currentCharCount = 0
  words.forEach((word, index) => {
    const wordEnd = convertTimeStringToSeconds(word.endTime)
    const wordString = word.word.trim()
    const wordCharCount = wordString.length

    if (currentSegment.startTime === '') {
      currentSegment.startTime = word.startTime
      currentSegment.endTime = word.endTime
      currentSegment.words.push({
        ...word,
        word: removePunctuation ? stripSubtitlePunctuation(word.word) : word.word,
      })
      currentCharCount += wordCharCount
      return // continue
    }
    const segmentStart = convertTimeStringToSeconds(currentSegment.startTime)

    const lastWordString = words[index - 1]?.word?.trim() || ''
    const isWordEnding = WORD_END_CHARACTERS.has(lastWordString.slice(-1))

    if (
      // Duração da legenda ultrapassará o tempo máximo
      (wordEnd - segmentStart) >= maxSecondsPerLine
      // Contagem de palavra ultrapassará o maximo
      || currentSegment.words.length >= maxWordsPerLine
      // Contagem de caracteres ultrapassará o maximo
      || currentCharCount >= maxCharactersPerLine
      // é o final de uma frase
      || (!cutPhrases && isWordEnding)
    ) {
      reorganizedSegments.push(currentSegment)
      currentSegment = {
        startTime: word.startTime,
        endTime: '',
        words: [],
      }
      currentCharCount = 0
    }

    currentSegment.endTime = word.endTime
    currentSegment.words.push({
      ...word,
      word: removePunctuation ? stripSubtitlePunctuation(word.word) : word.word,
    })
    currentCharCount += wordCharCount

    const isLastWord = index === (words.length - 1)
    if (isLastWord) {
      reorganizedSegments.push(currentSegment)
    }
  })

  return reorganizedSegments.map((segment, index) => ({
    id: index,
    ...segment,
    text: segment.words.map((w) => w.word.trim()).join(' '),
  }))
}

export default reorganizeWords